import React, { useEffect, useState, useCallback, useMemo } from "react";
import styles from "./WheelSpinTab.module.css";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { Icon } from "@iconify/react";
import {
  getWheelPoint,
  getPlayerBalance,
  getUsersByID,
} from "../../store/slices/user";

const MAX_WHEEL_PRIZES = 10;
const sliceColors = [
  "#ff006e",
  "#ffbe0b",
  "#fb5607",
  "#8338ec",
  "#3a86ff",
  "#c02b58",
  "#20bf55",
  "#ff9f1c",
  "#7209b7",
  "#06d6a0",
];

const toastVariants = {
  success: {
    backgroundColor: "#D1FAE5",
    border: "2px solid #10B981",
    textColor: "#065F46",
    icon: "✅",
  },
  info: {
    backgroundColor: "#FEF3C7",
    border: "2px solid #F59E0B",
    textColor: "#92400E",
    icon: "😔",
  },
  error: {
    backgroundColor: "#FEE2E2",
    border: "2px solid #EF4444",
    textColor: "#991B1B",
    icon: "⚠️",
  },
};

const ResultToast = ({ message, show, onClose, type = "success" }) => {
  useEffect(() => {
    if (!show) return;
    const timer = setTimeout(onClose, 4000);
    return () => clearTimeout(timer);
  }, [show, onClose]);

  if (!show) return null;

  const variant = toastVariants[type] || toastVariants.success;

  return (
    <div
      style={{
        position: "fixed",
        top: "20px",
        right: "20px",
        backgroundColor: variant.backgroundColor,
        border: variant.border,
        borderRadius: "12px",
        padding: "16px 20px",
        maxWidth: "320px",
        boxShadow: "0 10px 25px rgba(0, 0, 0, 0.15)",
        zIndex: 10000,
        animation: "slideIn 0.3s ease-out",
        display: "flex",
        alignItems: "center",
        gap: "12px",
      }}
    >
      <span style={{ fontSize: "20px" }}>{variant.icon}</span>
      <span style={{ color: variant.textColor, fontWeight: "500", flex: 1 }}>
        {message}
      </span>
      <button
        onClick={onClose}
        style={{
          background: "none",
          border: "none",
          fontSize: "24px",
          cursor: "pointer",
          color: variant.textColor,
          padding: "4px",
          marginLeft: "8px",
          borderRadius: "50%",
          width: "32px",
          height: "32px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          transition: "all 0.2s ease",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = "rgba(0, 0, 0, 0.1)";
          e.currentTarget.style.transform = "scale(1.1)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = "transparent";
          e.currentTarget.style.transform = "scale(1)";
        }}
      >
        ×
      </button>
      <style>
        {`
          @keyframes slideIn {
            from {
              transform: translateX(100%);
              opacity: 0;
            }
            to {
              transform: translateX(0);
              opacity: 1;
            }
          }
        `}
      </style>
    </div>
  );
};

const WheelSpinTab = () => {
  const dispatch = useDispatch();
  const { singleUsers, wheelPoint } = useSelector((s) => s.user);
  const user = singleUsers?.[0];
  // Block spin when user is under withdraw condition or has active promotion
  const withdrawConditionDetail = user?.withdrawConditionDetail;
  const promotionStatus = user?.PromotionStatus;
  const isPromoActive = promotionStatus === "ACTIVE";

  const [settings, setSettings] = useState(null);
  const [prizes, setPrizes] = useState(() => Array(MAX_WHEEL_PRIZES).fill(0));
  const [probabilities, setProbabilities] = useState(() =>
    Array(MAX_WHEEL_PRIZES).fill(0),
  );
  const [prizeTypes, setPrizeTypes] = useState(() =>
    Array(MAX_WHEEL_PRIZES).fill("credit"),
  );
  const [prizeLabels, setPrizeLabels] = useState(() =>
    Array(MAX_WHEEL_PRIZES).fill(""),
  );
  const [prizeImages, setPrizeImages] = useState(() =>
    Array(MAX_WHEEL_PRIZES).fill(""),
  );
  const [wheelBgImage, setWheelBgImage] = useState("");
  const [deg, setDeg] = useState(0);
  const [spinning, setSpinning] = useState(false);
  const [error, setError] = useState("");
  const [toast, setToast] = useState({
    show: false,
    message: "",
    type: "success",
  });

  const sectorAngle = useMemo(
    () => (prizes.length ? 360 / prizes.length : 0),
    [prizes.length],
  );

  const showToast = (message, type = "success") =>
    setToast({ show: true, message, type });
  const hideToast = () =>
    setToast({ show: false, message: "", type: "success" });

  const fetchSettings = useCallback(async () => {
    try {
      setError("");
      const res = await axios.get(
        "https://api.huay-huay.vip/setting/wheelsetting",
      );
      const list = Array.isArray(res?.data?.data) ? res.data.data : [];
      const first = list[0] || {};
      setSettings(first);
      const prizeData = Array.from({ length: MAX_WHEEL_PRIZES }, (_, idx) => {
        const key = idx + 1;
        return Number(first?.[`prizeAmount${key}`]) || 0;
      });
      const probData = Array.from({ length: MAX_WHEEL_PRIZES }, (_, idx) => {
        const key = idx + 1;
        const value = Number(first?.[`prizeProb${key}`]) || 0;
        return value / 100;
      });
      const typeData = Array.from({ length: MAX_WHEEL_PRIZES }, (_, idx) => {
        return first?.[`prizeType${idx + 1}`] ?? "credit";
      });
      const labelData = Array.from({ length: MAX_WHEEL_PRIZES }, (_, idx) => {
        return first?.[`prizeLabel${idx + 1}`] ?? "";
      });
      const imageData = Array.from({ length: MAX_WHEEL_PRIZES }, (_, idx) => {
        return first?.[`prizeImage${idx + 1}`] ?? "";
      });
      setPrizes(prizeData);
      setProbabilities(probData);
      setPrizeTypes(typeData);
      setPrizeLabels(labelData);
      setPrizeImages(imageData);
      setWheelBgImage(first?.wheelBgImage ?? "");
    } catch (e) {
      setError("โหลดการตั้งค่ากงล้อล้มเหลว ใช้ค่าเริ่มต้น");
      setPrizes([0, 5, 10, 15, 20, 30, 45, 60, 80, 100]);
      setProbabilities([
        0.25, 0.18, 0.14, 0.1, 0.08, 0.07, 0.06, 0.05, 0.04, 0.03,
      ]);
      setPrizeTypes(Array(MAX_WHEEL_PRIZES).fill("credit"));
      setPrizeLabels(Array(MAX_WHEEL_PRIZES).fill(""));
      setPrizeImages(Array(MAX_WHEEL_PRIZES).fill(""));
      setWheelBgImage("");
    }
  }, []);

  // Helper to normalize user's available points from various possible shapes
  const getUserPoints = useCallback(() => {
    const src = wheelPoint;
    if (typeof src === "number") return src;
    if (typeof src === "string" && !Number.isNaN(Number(src)))
      return Number(src);
    const item = Array.isArray(src) ? src[0] : src || {};
    const candidates = [
      item?.wheelPoint,
      item?.point,
      item?.points,
      item?.diamond,
      item?.balance,
    ];
    for (const v of candidates) {
      const n = typeof v === "string" ? Number(v) : v;
      if (typeof n === "number" && !Number.isNaN(n)) return n;
    }
    return 0;
  }, [wheelPoint]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);
  useEffect(() => {
    if (user?.id) dispatch(getWheelPoint(user.id));
  }, [user?.id, dispatch]);

  const spinAllowed = () => {
    if (!user || !settings) return false;
    // Disallow spin when on withdraw condition or promotion is ACTIVE
    if (withdrawConditionDetail) return false;
    if (isPromoActive) return false;
    const cost = Number(settings?.spinAmount ?? settings?.spinCost ?? 0);
    const current = getUserPoints();
    const hasProbability = probabilities.some((prob) => prob > 0);
    return current >= cost && !spinning && hasProbability;
  };

  const getRotationForWinningIndex = useCallback(
    (winningIndex, currentDeg) => {
      const normalizedDeg = ((currentDeg % 360) + 360) % 360;
      const targetAngle = winningIndex * sectorAngle;
      const deltaToTarget = (((targetAngle - normalizedDeg) % 360) + 360) % 360;
      const spins = Math.floor(Math.random() * 6) + 10;
      const jitterLimit = Math.max(0, sectorAngle / 2 - 3);
      const jitter = (Math.random() - 0.5) * 2 * jitterLimit;
      return 360 * spins + deltaToTarget + jitter;
    },
    [sectorAngle],
  );

  const spin = async () => {
    if (!spinAllowed()) return;
    setError("");
    setSpinning(true);

    try {
      const response = await axios.post(
        `https://api.huay-huay.vip/wheel/spining/${user.id}`,
      );
      const payload = response?.data || {};
      const winningIndex = Number(payload?.winningIndex);
      const resolvedWinningIndex = Number.isInteger(winningIndex)
        ? winningIndex
        : -1;

      if (
        resolvedWinningIndex < 0 ||
        resolvedWinningIndex >= prizes.length ||
        (probabilities[resolvedWinningIndex] || 0) <= 0
      ) {
        throw new Error("INVALID_WINNING_INDEX");
      }

      const totalRotation = getRotationForWinningIndex(
        resolvedWinningIndex,
        deg,
      );
      setDeg((current) => current + totalRotation);

      const prizeAmount = Number(payload?.prize);
      const hasPrize =
        typeof payload?.hasPrize === "boolean"
          ? payload.hasPrize
          : prizeAmount > 0;

      const resolvedPrizeAmount = Number.isNaN(prizeAmount)
        ? prizes[resolvedWinningIndex] || 0
        : prizeAmount;
      const spinResult = {
        prize: resolvedPrizeAmount,
        hasPrize,
        message:
          payload?.message ||
          (hasPrize
            ? `ยินดีด้วย! คุณได้รับ ${resolvedPrizeAmount}฿`
            : "เสียใจด้วย คุณไม่ได้รางวัล"),
        type: hasPrize ? "success" : "info",
      };

      setTimeout(() => {
        setError("");
        showToast(spinResult.message, spinResult.type);

        if (user?.id) {
          dispatch(getWheelPoint(user.id));
          dispatch(getPlayerBalance(user));
          dispatch(getUsersByID(user));
        }
        setSpinning(false);
      }, 5200);
    } catch (requestError) {
      const errorMessage =
        requestError?.message === "INVALID_WINNING_INDEX"
          ? "ผลการหมุนไม่ถูกต้อง กรุณาลองใหม่อีกครั้ง"
          : requestError?.response?.data?.message ||
            "เกิดข้อผิดพลาดในการหมุนวงล้อ กรุณาลองใหม่อีกครั้ง";
      setError(errorMessage);
      showToast(errorMessage, "error");
      setSpinning(false);
    }
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <div className={styles.iconWrapper}>
          <Icon icon="mdi:progress-clock" className={styles.icon} />
        </div>
        <h2 className={styles.title}>กงล้อพารวย</h2>
        <p className={styles.subtitle}>สะสมเพชรเพื่อหมุนรับของรางวัล</p>
        {error && <div className={styles.errorMsg}>{error}</div>}
      </div>

      <div className={styles.wheelSection}>
        <div className={styles.wheelOuter}></div>
        <div
          className={`${styles.wheel}${wheelBgImage ? ` ${styles.wheelWithBg}` : ""}`}
        >
          <div
            className={styles.inner}
            style={{ transform: `rotate(${deg}deg)` }}
          >
            {wheelBgImage ? (
              <>
                <img
                  src={`https://api.huay-huay.vip${wheelBgImage}`}
                  className={styles.wheelBgImg}
                  alt="กงล้อ"
                />
                {prizes.map((p, i) => {
                  const angleRad = (i * -sectorAngle * Math.PI) / 180;
                  const r = 29;
                  const x = 50 + r * Math.sin(angleRad);
                  const y = 50 - r * Math.cos(angleRad);
                  const isItem = prizeTypes[i] === "item";
                  const imgSrc =
                    isItem && prizeImages[i]
                      ? `https://api.huay-huay.vip${prizeImages[i]}`
                      : null;
                  return (
                    <span
                      key={`overlay-${i}`}
                      className={`${styles.prizeOverlay}${
                        isItem ? "" : ` ${styles.prizeGold}`
                      }`}
                      style={{
                        left: `${x}%`,
                        top: `${y}%`,
                        transform: `translate(-50%, -50%) rotate(${
                          i * -sectorAngle + 90
                        }deg)`,
                      }}
                    >
                      {imgSrc ? (
                        <img
                          src={imgSrc}
                          className={styles.overlayItemImg}
                          alt={prizeLabels[i] || "item"}
                        />
                      ) : isItem ? (
                        prizeLabels[i] || "🎁"
                      ) : (
                        `${p}฿`
                      )}
                    </span>
                  );
                })}
              </>
            ) : (
              prizes.map((p, i) => (
                <div
                  key={`slice-${i}`}
                  className={styles.slice}
                  style={{
                    transform: `rotate(${i * -sectorAngle}deg)`,
                    borderColor: `${
                      sliceColors[i % sliceColors.length]
                    } transparent`,
                  }}
                >
                  <span
                    className={`${styles.prize}${
                      prizeTypes[i] === "item" ? "" : ` ${styles.prizeGold}`
                    }`}
                    style={
                      prizeTypes[i] === "item"
                        ? { fontSize: "calc(var(--wheel-size) / 22)" }
                        : undefined
                    }
                  >
                    {prizeTypes[i] === "item"
                      ? prizeLabels[i] || "🎁"
                      : Number(p) || Number(p) === 0
                        ? `${p}฿`
                        : "-"}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>
        <div className={styles.arrow}></div>
        <button
          className={styles.spinButton}
          onClick={spin}
          disabled={!spinAllowed()}
        >
          {spinning ? "กำลังหมุน..." : "หมุน"}
        </button>
      </div>

      <div className={styles.statusRow}>
        <div className={styles.statusBox}>
          <span className={styles.statusLabel}>แต้มเพชร</span>
          <span className={styles.statusValue}>{getUserPoints()}</span>
        </div>
        <div className={styles.statusBox}>
          <span className={styles.statusLabel}>หมุนครั้งละ</span>
          <span className={styles.statusValue}>
            {Number(settings?.spinAmount ?? settings?.spinCost ?? 0)}
          </span>
        </div>
        <div className={styles.statusBox}>
          <span className={styles.statusLabel}>สถานะ</span>
          <span className={styles.statusValue}>
            {spinAllowed() ? "พร้อมหมุน" : "ยังไม่พร้อม"}
          </span>
        </div>
      </div>

      {prizeTypes.some(
        (t, i) => t === "item" && (prizeLabels[i] || prizeImages[i]),
      ) && (
        <div className={styles.itemPrizesSection}>
          <p className={styles.itemPrizesHeading}>รางวัลสิ่งของ</p>
          <div className={styles.itemPrizesGrid}>
            {prizeTypes.map((t, i) => {
              if (t !== "item") return null;
              const label = prizeLabels[i];
              const img = prizeImages[i];
              if (!label && !img) return null;
              return (
                <div key={`item-prize-${i}`} className={styles.itemPrizeCard}>
                  {img && (
                    <img
                      src={`https://api.huay-huay.vip${img}`}
                      className={styles.itemPrizeImg}
                      alt={label || "item"}
                    />
                  )}
                  {label && (
                    <span className={styles.itemPrizeLabel}>{label}</span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
      <ResultToast
        message={toast.message}
        show={toast.show}
        onClose={hideToast}
        type={toast.type}
      />
    </div>
  );
};

export default WheelSpinTab;
